import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'

import Pane from '@/components/pane'
import Padding from '@/components/padding'
import PrivateButton from '@/components/private-button'
import PermissionButton from '@/components/permission-button'
import DataDictFinder from '@/components/data-dict-finder'
import Money from '@/components/money'
import Download from '@/components/download'

import './css/index.less'

import Print from 'vue-print-nb'

import {
  Button, Menu, Layout, Icon, Card, Table, Tabs, Form,
  Row, Col, Alert, Input, Checkbox, Select, Modal, Descriptions,
  Space, Divider, Dropdown, Badge, Avatar, Tree, message,
  Result, Upload, Progress, Breadcrumb, Statistic, List,
  Steps, Radio, InputNumber, Tooltip, ConfigProvider, FormModel,
  Empty, Pagination, DatePicker, TimePicker, Tag, Timeline,
  Carousel, Transfer, Cascader, Skeleton, Slider, Spin,
  Switch, Popconfirm, Popover, AutoComplete, Collapse, Calendar
} from 'ant-design-vue'

// 常用方法
import encrypt from './utils/encrypt'
import { copy } from './utils/clipboard'
import { getPermission } from '@/utils/permission'
import { imgCrop } from '@/utils/image'
import { close } from '@/utils/cache'
import { selectFilterOption, ratioFormatter, inputNumberFormat } from '@/utils/antdv'
import { getDateString } from '@/utils/time'
import { getFileName } from '@/utils/file'
import { fetchDetail } from '@/api/setting/user'

import { numberFormat } from '@/utils/num'

// 精确计算方法
import accurate from "accurate";
Vue.prototype.$calc = accurate

Vue.config.productionTip = false

Vue
  .use(Button).use(Menu).use(Layout).use(Icon).use(Card).use(Table)
  .use(Tabs).use(Form).use(Row).use(Col).use(Alert).use(Input).use(Checkbox)
  .use(Select).use(Modal).use(Descriptions).use(Space).use(Divider).use(Dropdown)
  .use(Badge).use(Avatar).use(Tree).use(Result).use(Upload).use(Progress)
  .use(Breadcrumb).use(Statistic).use(List).use(Steps).use(Radio)
  .use(InputNumber).use(Tooltip).use(ConfigProvider).use(FormModel)
  .use(Empty).use(Pagination).use(DatePicker).use(TimePicker).use(Tag).use(Timeline)
  .use(Carousel).use(Transfer).use(Cascader).use(Skeleton).use(Slider).use(Spin)
  .use(Switch).use(Popconfirm).use(Popover).use(AutoComplete).use(Collapse)
  .use(Calendar)
  .use(Print)

Vue.prototype.$message = message;
Vue.prototype.$confirm = Modal.confirm;
Vue.prototype.$error = Modal.error;
Vue.prototype.$success = Modal.success;
Vue.prototype.$info = Modal.info;


Vue.component('Pane', Pane) // 页面的头部面板
Vue.component('PrivateButton', PrivateButton) // 页面内的权限按钮
Vue.component('PermissionButton', PermissionButton) // 页面内的权限按钮
Vue.component("Padding", Padding) // 页面常用的间距组件
Vue.component('DataDictFinder', DataDictFinder) // 数据字典查找器
Vue.component("Money", Money)
Vue.component('Download', Download)

Vue.prototype.$getPermission = getPermission;
Vue.prototype.$encrypt = encrypt;
Vue.prototype.$copy = copy;
Vue.prototype.$imgCrop = imgCrop;
Vue.prototype.$close = close
Vue.prototype.$selectFilterOption = selectFilterOption;
Vue.prototype.$ratioFormatter = ratioFormatter;
Vue.prototype.$inputNumberFormat = inputNumberFormat;
Vue.prototype.$getDateString = getDateString;
Vue.prototype.$getFileName = getFileName;
Vue.prototype.$numberFormat = numberFormat;
Vue.prototype.$eventBus = new Vue()

// ==== activated 静默返回机制（全站） ====
// 背景：layout 的 keep-alive 缓存生效后，页面切回会触发 activated，很多页面在
// activated 里重拉数据，导致"关闭 tab 回到焦点页"时页面重新请求接口、看起来像刷新。
// 此处在全局包装 activated：tabs 组件关闭 tab 跳回目标页前会在 sessionStorage 写入
// __silentReturnPath（目标页 path），包装层检测到本次激活即为目标页静默返回时，
// 跳过该组件全部 activated 钩子——不重拉数据、保持离开时的状态。
// 表单提交成功后的正常跳转返回不设标记，activated 照常执行，"提交后列表刷新"的业务
// 行为完整保留。keep-alive 组件首次创建也会触发 activated（页面初始化依赖），首次一律放行。
Vue.mixin({
  created() {
    const opts = this.$options
    // 仅包装定义了 activated 的组件；$options 在同组件多实例间共享，只需包装一次
    if (!opts.activated || !opts.activated.length || opts.__activatedWrapped) {
      return
    }
    opts.__activatedWrapped = true
    const originalHooks = opts.activated.slice()
    opts.activated = [
      function activated() {
        if (!this.__firstActivatedDone) {
          // 首次激活：页面初始化钩子必须执行
          this.__firstActivatedDone = true
          originalHooks.forEach((h) => h.call(this))
          return
        }
        const raw = sessionStorage.getItem('__silentReturnPath')
        if (raw !== null) {
          // 标记格式：path|时间戳。5 秒内有效，防止目标页无 activated 时标记残留，
          // 之后误跳过同路径页面的初始化
          const sep = raw.lastIndexOf('|')
          const path = sep > -1 ? raw.slice(0, sep) : raw
          const ts = sep > -1 ? Number(raw.slice(sep + 1)) : 0
          const fresh = Date.now() - ts < 5000
          if (!fresh) {
            sessionStorage.removeItem('__silentReturnPath')
          } else if (path === this.$route.path) {
            // 关闭 tab 的静默返回：跳过 activated（不重拉数据）
            sessionStorage.removeItem('__silentReturnPath')
            return
          }
        }
        originalHooks.forEach((h) => h.call(this))
      },
    ]
  },
})

// ==== 用户信息定时刷新 ====
// 原逻辑在 router.beforeEach 中每次导航都调 /user-service/user/query 拉取用户详情，
// 导航频繁时请求量大。现改为定时刷新：应用启动时的首次拉取由 layout/index.vue 的
// mounted 完成（同时初始化权限、菜单、数据字典），之后每 60 秒静默刷新一次
// employeeVo；未登录（localStorage 无 user）时自动跳过，登出后不再请求。
const USER_REFRESH_INTERVAL = 60 * 1000
setInterval(() => {
  const user = JSON.parse(localStorage.getItem('user'))
  if (!user || !user.uuid) {
    return
  }
  fetchDetail({ uuid: user.uuid }).then((v) => {
    store.commit('auth/setUser', { ...user, employeeVo: v.employeeVo })
  }).catch(() => {})
}, USER_REFRESH_INTERVAL)

new Vue({
  router,
  store,
  render: h => h(App)
}).$mount('#app')
